/**
 * Permission checking utilities for the multi-tenant management system
 */

import { UserContext } from '../types/tenant';

export interface PermissionChecker {
  canCreateTenant: boolean;
  canEditTenant: (tenantId: string) => boolean;
  canDeleteTenant: (tenantId: string) => boolean;
  canCreateLocation: (tenantId: string) => boolean;
  canEditLocation: (tenantId: string, locationId: string) => boolean;
  canDeleteLocation: (tenantId: string, locationId: string) => boolean;
  canCreateServer: (tenantId: string, locationId: string) => boolean;
  canEditServer: (tenantId: string, locationId: string, serverId: string) => boolean;
  canDeleteServer: (tenantId: string, locationId: string, serverId: string) => boolean;
  canCreateManager: (tenantId: string, locationId?: string) => boolean;
  canEditManager: (managerId: string) => boolean;
  canDeleteManager: (managerId: string) => boolean;
}

export function createPermissionChecker(user: UserContext): PermissionChecker {
  // Case-insensitive role comparison to handle backend variations
  const roleLower = (user.role || '').toLowerCase();
  const isAdmin = roleLower === 'admin';
  const isCorporateManager = roleLower === 'corporate';
  const isStoreManager = roleLower === 'manager';

  return {
    // Tenant permissions
    canCreateTenant: isAdmin,
    canEditTenant: (tenantId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      return false;
    },
    canDeleteTenant: (tenantId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      return false;
    },

    // Location permissions
    canCreateLocation: (tenantId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      return false;
    },
    canEditLocation: (tenantId: string, locationId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      if (isStoreManager) return user.locationId === locationId;
      return false;
    },
    canDeleteLocation: (tenantId: string, locationId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      return false;
    },

    // Server permissions
    canCreateServer: (tenantId: string, locationId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      if (isStoreManager) return user.locationId === locationId;
      return false;
    },
    canEditServer: (tenantId: string, locationId: string, serverId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      if (isStoreManager) return user.locationId === locationId;
      return false;
    },
    canDeleteServer: (tenantId: string, locationId: string, serverId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      if (isStoreManager) return user.locationId === locationId;
      return false;
    },

    // Manager permissions
    canCreateManager: (tenantId: string, locationId?: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return user.tenantId === tenantId;
      return false;
    },
    canEditManager: (managerId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return true; // Can edit managers in their tenant
      return false;
    },
    canDeleteManager: (managerId: string) => {
      if (isAdmin) return true;
      if (isCorporateManager) return true; // Can delete managers in their tenant
      return false;
    }
  };
}